import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { authService } from '../../services/api';

const FONT = "'Plus Jakarta Sans', sans-serif";
const PIN_LENGTH = 6;

const LockScreenPage = () => {
    const navigate = useNavigate();
    const { username, employeeId, tenantId } = useAuth();
    const { t } = useLanguage();

    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const sessionId = sessionStorage.getItem('lockScreen_sessionId') || '';
    const station = sessionStorage.getItem('lockScreen_station') || '';
    const returnPath = sessionStorage.getItem('lockScreen_returnPath') || '/functions';

    const handleUnlock = useCallback(async (fullPin) => {
        setLoading(true);
        setError('');
        try {
            const result = await authService.employeeLogin(tenantId, fullPin);
            if (result.employee_id === employeeId || String(result.employee_id) === String(employeeId)) {
                navigate(returnPath);
            } else {
                setError(t('Wrong PIN. This station is locked by {name}.', { name: username }));
                setPin('');
            }
        } catch (err) {
            setError(t('Wrong PIN. Please try again.'));
            setPin('');
        } finally {
            setLoading(false);
        }
    }, [tenantId, employeeId, username, returnPath, navigate, t]);

    const handleDigit = useCallback((digit) => {
        if (loading) return;
        setError('');
        const newPin = pin + digit;
        setPin(newPin);
        if (newPin.length === PIN_LENGTH) {
            handleUnlock(newPin);
        }
    }, [pin, loading, handleUnlock]);

    const handleBackspace = useCallback(() => {
        if (loading) return;
        setPin(prev => prev.slice(0, -1));
        setError('');
    }, [loading]);

    // Keyboard input support
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key >= '0' && e.key <= '9') {
                handleDigit(e.key);
            } else if (e.key === 'Backspace') {
                handleBackspace();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleDigit, handleBackspace]);

    const handleSwitchUser = () => {
        sessionStorage.removeItem('lockScreen_sessionId');
        sessionStorage.removeItem('lockScreen_station');
        sessionStorage.removeItem('lockScreen_returnPath');
        navigate('/functions');
    };

    const stationLabel = station && sessionId
        ? `${station} \u2022 ${sessionId}`
        : station || '';

    const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'];

    return (
        <div style={{
            background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: FONT,
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 20,
            }}>
                {/* Brand mark */}
                <div style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    backgroundColor: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                </div>

                {/* Station info */}
                {stationLabel && (
                    <div style={{
                        fontSize: 12,
                        color: '#64748b',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                    }}>
                        {stationLabel}
                    </div>
                )}

                {/* Employee name */}
                <div style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#f1f5f9',
                }}>
                    {username || t('Employee')}
                </div>

                {/* Enter PIN label */}
                <div style={{
                    fontSize: 11,
                    color: '#475569',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                }}>
                    {t('Enter PIN to unlock')}
                </div>

                {/* PIN dots */}
                <div style={{
                    display: 'flex',
                    gap: 12,
                    margin: '4px 0',
                }}>
                    {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                        <div key={i} style={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            backgroundColor: i < pin.length ? '#22c55e' : 'transparent',
                            border: i < pin.length ? '2px solid #22c55e' : '2px solid #334155',
                            transition: 'all 0.15s ease',
                        }} />
                    ))}
                </div>

                {/* Error message */}
                {error && (
                    <div style={{
                        fontSize: 12,
                        color: '#ef4444',
                        fontWeight: 500,
                        textAlign: 'center',
                        maxWidth: 240,
                    }}>
                        {error}
                    </div>
                )}

                {/* Numeric keypad */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 60px)',
                    gap: 12,
                    marginTop: 8,
                }}>
                    {digits.map((d, i) => {
                        if (d === '') {
                            return <div key={i} style={{ width: 60, height: 60 }} />;
                        }
                        if (d === 'back') {
                            return (
                                <button
                                    key={i}
                                    onClick={handleBackspace}
                                    disabled={loading}
                                    style={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        backgroundColor: '#1e293b',
                                        border: '1px solid #334155',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.15s ease',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#334155'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1e293b'}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                                        <line x1="18" y1="9" x2="12" y2="15" />
                                        <line x1="12" y1="9" x2="18" y2="15" />
                                    </svg>
                                </button>
                            );
                        }
                        return (
                            <button
                                key={i}
                                onClick={() => handleDigit(d)}
                                disabled={loading || pin.length >= PIN_LENGTH}
                                style={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: '50%',
                                    backgroundColor: '#1e293b',
                                    border: '1px solid #334155',
                                    fontSize: 22,
                                    fontWeight: 600,
                                    color: '#e2e8f0',
                                    cursor: 'pointer',
                                    fontFamily: FONT,
                                    transition: 'background-color 0.15s ease',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#334155'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1e293b'}
                            >
                                {d}
                            </button>
                        );
                    })}
                </div>

                {/* Action links */}
                <div style={{
                    display: 'flex',
                    gap: 24,
                    marginTop: 16,
                }}>
                    <button
                        onClick={handleSwitchUser}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: 12,
                            color: '#475569',
                            cursor: 'pointer',
                            fontFamily: FONT,
                            fontWeight: 500,
                        }}
                    >
                        {t('Switch User')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LockScreenPage;
